import React, { useEffect } from "react"
import { useTranslation } from "react-i18next"
import { Container, Row, Col, Card, CardBody } from "reactstrap"
import { Link } from "react-router-dom"
import FeatherIcon from "feather-icons-react"
import CountUp from "react-countup"
import logo from "../../assets/images/logo.png"

const AboutPage = () => {
    const { t } = useTranslation()

    useEffect(() => {
        window.scrollTo(0, 0)
    }, [])

    const values = [
        { icon: "target", title: t("about.values.innovation.title"), desc: t("about.values.innovation.desc") },
        { icon: "shield", title: t("about.values.trust.title"), desc: t("about.values.trust.desc") },
        { icon: "users", title: t("about.values.customer.title"), desc: t("about.values.customer.desc") },
        { icon: "zap", title: t("about.values.quality.title"), desc: t("about.values.quality.desc") },
    ]

    const stats = [
        { number: 150, suffix: "+", label: t("about.stats.clients") },
        { number: 9, suffix: "", label: t("about.stats.modules") },
        { number: 50000, suffix: "+", label: t("about.stats.users") },
        { number: 7, suffix: "/24", label: t("about.stats.support") },
    ]

    return (
        <React.Fragment>
            {/* Hero Section */}
            <section
                className="bg-half-170 d-table w-100"
                style={{
                    background: "linear-gradient(135deg, #1E3A5F 0%, #0D1F33 100%)"
                }}
            >
                <Container>
                    <Row className="justify-content-center">
                        <Col lg={10} className="text-center">
                            <div className="title-heading">
                                <span
                                    className="badge rounded-pill mb-3"
                                    style={{ backgroundColor: "#E65100", padding: "8px 16px" }}
                                >
                                    <FeatherIcon icon="info" style={{ width: "14px", height: "14px" }} className="me-1" />
                                    Netta Software
                                </span>
                                <h1 className="heading text-white mb-3">
                                    {t("about.hero.title")}
                                </h1>
                                <p className="text-white-50 para-desc mx-auto mb-0">
                                    {t("about.hero.subtitle")}
                                </p>
                            </div>
                        </Col>
                    </Row>
                </Container>
            </section>

            {/* Mission Section */}
            <section className="section">
                <Container>
                    <Row className="align-items-center">
                        <Col lg={6} md={6}>
                            <div
                                className="rounded-4 p-5 position-relative"
                                style={{
                                    background: "rgba(255, 255, 255, 0.95)",
                                    backdropFilter: "blur(10px)",
                                    minHeight: "400px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.15), 0 0 40px rgba(230, 81, 0, 0.1)",
                                    border: "1px solid rgba(230, 81, 0, 0.1)"
                                }}
                            >
                                {/* Decorative elements */}
                                <div
                                    style={{
                                        position: "absolute",
                                        top: "-20px",
                                        right: "-20px",
                                        width: "100px",
                                        height: "100px",
                                        background: "linear-gradient(135deg, #E65100 0%, #FF8A50 100%)",
                                        borderRadius: "50%",
                                        opacity: "0.1"
                                    }}
                                />
                                <div
                                    style={{
                                        position: "absolute",
                                        bottom: "-30px",
                                        left: "-30px",
                                        width: "150px",
                                        height: "150px",
                                        background: "linear-gradient(135deg, #1E3A5F 0%, #0D1F33 100%)",
                                        borderRadius: "50%",
                                        opacity: "0.05"
                                    }}
                                />
                                <div className="text-center position-relative">
                                    <div
                                        className="mb-4 mx-auto d-flex align-items-center justify-content-center"
                                        style={{
                                            width: "120px",
                                            height: "120px",
                                            background: "linear-gradient(135deg, #E65100 0%, #BF360C 100%)",
                                            borderRadius: "24px",
                                            boxShadow: "0 15px 35px rgba(230, 81, 0, 0.3)"
                                        }}
                                    >
                                        <img src={logo} alt="Netta" style={{ height: "60px", filter: "brightness(0) invert(1)" }} />
                                    </div>
                                    <h2 className="mb-2" style={{ color: "#1E3A5F" }}>Netta Software</h2>
                                    <p className="text-muted mb-0">{t("about.mission.tagline")}</p>
                                </div>
                            </div>
                        </Col>
                        <Col lg={6} md={6} className="mt-4 mt-lg-0">
                            <div className="ms-lg-4">
                                <span className="badge bg-soft-primary rounded-pill px-3 py-2 mb-3">
                                    {t("about.mission.badge")}
                                </span>
                                <h3 className="mb-4">{t("about.mission.title")}</h3>
                                <p className="text-muted">
                                    {t("about.mission.desc1")}
                                </p>
                                <p className="text-muted mb-4">
                                    {t("about.mission.desc2")}
                                </p>
                                <ul className="list-unstyled mb-0">
                                    <li className="d-flex align-items-center mb-2">
                                        <FeatherIcon icon="check-circle" className="text-primary me-2" style={{ width: "20px", height: "20px" }} />
                                        <span>{t("about.mission.point1")}</span>
                                    </li>
                                    <li className="d-flex align-items-center mb-2">
                                        <FeatherIcon icon="check-circle" className="text-primary me-2" style={{ width: "20px", height: "20px" }} />
                                        <span>{t("about.mission.point2")}</span>
                                    </li>
                                    <li className="d-flex align-items-center">
                                        <FeatherIcon icon="check-circle" className="text-primary me-2" style={{ width: "20px", height: "20px" }} />
                                        <span>{t("about.mission.point3")}</span>
                                    </li>
                                </ul>
                            </div>
                        </Col>
                    </Row>
                </Container>
            </section>

            {/* Stats Section */}
            <section
                className="section py-5"
                style={{ background: "linear-gradient(135deg, #1E3A5F 0%, #0D1F33 100%)" }}
            >
                <Container>
                    <Row>
                        {stats.map((stat, idx) => (
                            <Col lg={3} md={6} className="text-center py-4" key={idx}>
                                <h2 className="text-white mb-1">
                                    <CountUp end={stat.number} duration={3} />{stat.suffix}
                                </h2>
                                <p className="text-white-50 mb-0">{stat.label}</p>
                            </Col>
                        ))}
                    </Row>
                </Container>
            </section>

            {/* Values Section */}
            <section className="section bg-light">
                <Container>
                    <Row className="justify-content-center">
                        <Col lg={8} className="text-center mb-5">
                            <h3 className="mb-3">{t("about.values.title")}</h3>
                            <p className="text-muted mb-0">{t("about.values.subtitle")}</p>
                        </Col>
                    </Row>
                    <Row>
                        {values.map((value, idx) => (
                            <Col lg={3} md={6} className="mt-4 pt-2" key={idx}>
                                <Card className="border-0 shadow rounded h-100 text-center">
                                    <CardBody className="p-4">
                                        <div
                                            className="rounded-circle d-inline-flex align-items-center justify-content-center mb-4"
                                            style={{
                                                width: "70px",
                                                height: "70px",
                                                backgroundColor: "rgba(230, 81, 0, 0.1)"
                                            }}
                                        >
                                            <FeatherIcon
                                                icon={value.icon}
                                                className="text-primary"
                                                style={{ width: "32px", height: "32px" }}
                                            />
                                        </div>
                                        <h5 className="mb-3">{value.title}</h5>
                                        <p className="text-muted mb-0">{value.desc}</p>
                                    </CardBody>
                                </Card>
                            </Col>
                        ))}
                    </Row>
                </Container>
            </section>

            {/* Why Choose Us Section */}
            <section className="section">
                <Container>
                    <Row className="align-items-center">
                        <Col lg={6} md={6}>
                            <div className="me-lg-4">
                                <h3 className="mb-4">{t("about.why.title")}</h3>
                                <p className="text-muted mb-4">{t("about.why.desc")}</p>

                                <div className="d-flex align-items-start mb-3">
                                    <div
                                        className="rounded d-flex align-items-center justify-content-center me-3"
                                        style={{ width: "50px", height: "50px", backgroundColor: "rgba(230, 81, 0, 0.1)", flexShrink: 0 }}
                                    >
                                        <FeatherIcon icon="layers" className="text-primary" style={{ width: "24px", height: "24px" }} />
                                    </div>
                                    <div>
                                        <h6 className="mb-1">{t("about.why.feature1.title")}</h6>
                                        <p className="text-muted mb-0 small">{t("about.why.feature1.desc")}</p>
                                    </div>
                                </div>

                                <div className="d-flex align-items-start mb-3">
                                    <div
                                        className="rounded d-flex align-items-center justify-content-center me-3"
                                        style={{ width: "50px", height: "50px", backgroundColor: "rgba(46, 125, 50, 0.1)", flexShrink: 0 }}
                                    >
                                        <FeatherIcon icon="refresh-cw" style={{ width: "24px", height: "24px", color: "#2E7D32" }} />
                                    </div>
                                    <div>
                                        <h6 className="mb-1">{t("about.why.feature2.title")}</h6>
                                        <p className="text-muted mb-0 small">{t("about.why.feature2.desc")}</p>
                                    </div>
                                </div>

                                <div className="d-flex align-items-start">
                                    <div
                                        className="rounded d-flex align-items-center justify-content-center me-3"
                                        style={{ width: "50px", height: "50px", backgroundColor: "rgba(21, 101, 192, 0.1)", flexShrink: 0 }}
                                    >
                                        <FeatherIcon icon="headphones" style={{ width: "24px", height: "24px", color: "#1565C0" }} />
                                    </div>
                                    <div>
                                        <h6 className="mb-1">{t("about.why.feature3.title")}</h6>
                                        <p className="text-muted mb-0 small">{t("about.why.feature3.desc")}</p>
                                    </div>
                                </div>
                            </div>
                        </Col>
                        <Col lg={6} md={6} className="mt-4 mt-lg-0">
                            <div
                                className="rounded-4 p-5"
                                style={{
                                    background: "linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)",
                                    minHeight: "400px"
                                }}
                            >
                                <Row className="g-4">
                                    <Col xs={6}>
                                        <div className="bg-white rounded p-4 text-center shadow-sm">
                                            <FeatherIcon icon="users" className="text-primary mb-2" style={{ width: "32px", height: "32px" }} />
                                            <h6 className="mb-0">{t("about.features.hrm")}</h6>
                                        </div>
                                    </Col>
                                    <Col xs={6}>
                                        <div className="bg-white rounded p-4 text-center shadow-sm">
                                            <FeatherIcon icon="folder" className="text-success mb-2" style={{ width: "32px", height: "32px" }} />
                                            <h6 className="mb-0">{t("about.features.project")}</h6>
                                        </div>
                                    </Col>
                                    <Col xs={6}>
                                        <div className="bg-white rounded p-4 text-center shadow-sm">
                                            <FeatherIcon icon="briefcase" className="text-info mb-2" style={{ width: "32px", height: "32px" }} />
                                            <h6 className="mb-0">{t("about.features.crm")}</h6>
                                        </div>
                                    </Col>
                                    <Col xs={6}>
                                        <div className="bg-white rounded p-4 text-center shadow-sm">
                                            <FeatherIcon icon="file-text" className="text-warning mb-2" style={{ width: "32px", height: "32px" }} />
                                            <h6 className="mb-0">{t("about.features.docs")}</h6>
                                        </div>
                                    </Col>
                                </Row>
                            </div>
                        </Col>
                    </Row>
                </Container>
            </section>

            {/* CTA Section */}
            <section
                className="section bg-primary"
                style={{ background: "linear-gradient(135deg, #E65100 0%, #BF360C 100%)" }}
            >
                <Container>
                    <Row className="justify-content-center">
                        <Col lg={8} className="text-center">
                            <h4 className="text-white mb-3">
                                {t("about.cta.title")}
                            </h4>
                            <p className="text-white-50 mb-4">
                                {t("about.cta.desc")}
                            </p>
                            <Link to="/contact-us" className="btn btn-light">
                                <FeatherIcon icon="mail" className="fea icon-sm me-2" />
                                {t("about.cta.button")}
                            </Link>
                        </Col>
                    </Row>
                </Container>
            </section>
        </React.Fragment>
    )
}

export default AboutPage
